import { arrayOf, bool, func, number, shape, string } from "prop-types";

import styled from "styled-components/macro";

import FnrCheckNReveal from "UI/FnrCheckNReveal/FnrCheckNReveal";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrCheckbox from "UI/FnrEnrichedCheckbox/FnrCheckbox";
import FnrTypography from "UI/FnrTypography";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap/FnrVerticalGap";
import { colors } from "UI/colors";

OtherNetworks.propTypes = {
  networks: arrayOf(shape({ ID: number, name: string, icon: string }))
    .isRequired,
  // The IDs of the currently-selected networks.
  value: arrayOf(number).isRequired,
  onChange: func.isRequired,
  share: bool.isRequired,
  onShareChange: func.isRequired,
};

/**
 * The "update this on other networks too" control: an enriched checkbox that
 * reveals a per-network list of enriched checkboxes, matching the app's
 * FnrEnrichedCheckbox panel pattern (see CreateGroup's SmartGroupOption).
 * Rendered only when the member actually has other networks to sync to.
 */
export default function OtherNetworks({
  networks,
  value,
  onChange,
  share,
  onShareChange,
}) {
  const toggleNetwork = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  return (
    <Panel>
      <FnrCheckNReveal
        on={share}
        toggleOn={() => onShareChange(!share)}
        addMargin
        revealedChildren={
          <FnrVerticalGap gap={10}>
            {networks.map((network) => (
              <FnrCheckbox
                key={network.ID}
                on={value.includes(network.ID)}
                toggleOn={() => toggleNetwork(network.ID)}
              >
                <NetworkRow>
                  {network.icon ? (
                    <NetworkIcon
                      src={network.icon}
                      alt=""
                      // Network photos are decorative and can be missing/404 in
                      // some networks; drop the img rather than show a broken
                      // placeholder next to the name.
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : null}
                  <FnrTypography>{network.name}</FnrTypography>
                </NetworkRow>
              </FnrCheckbox>
            ))}
          </FnrVerticalGap>
        }
      >
        <ShareLabel>
          <FnrTypography>Update this on other networks too</FnrTypography>
          <FnrContainedIcon noPadding icon="fa-sitemap fad" size="decorative2" />
        </ShareLabel>
      </FnrCheckNReveal>
    </Panel>
  );
}

const Panel = styled.div`
  margin-top: 12px;
  /* 8px matches the address field group so both inner boxes nest concentrically
     inside the card panel (whose radius is padding + 8; see IIGAddressCard). */
  border-radius: 8px;
  border: 1px solid ${colors.fnBorderGray2};
  padding: 12px 16px;
  background: #fff;
`;

// Fills the checkbox row so the decorative icon rides the far right edge, the
// way SmartGroupOption seats its branch icon.
const ShareLabel = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const NetworkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const NetworkIcon = styled.img`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;
